import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import tracker from '../api/tracker';
import useNotification from '../hooks/useNotificaction';

/**
 * CommentReplyBox
 *
 * Props:
 *  - hashid         {string}  — hashid of the parent record
 *  - endpoint       {string}  — API prefix: 'baptism' | 'docRequests'
 *  - existingReply  {object|null} — { body, replied_by_name, sent_at, email_status, email_sent_to }
 *  - recipientEmail {string}  — shown to admin so they know who will receive the email
 *  - onReplied      {function} — optional callback after a successful reply
 */
export default function CommentReplyBox({ hashid, endpoint, existingReply: initialReply, recipientEmail, onReplied }) {
  const { t } = useTranslation();
  const [sendNotification] = useNotification();
  const [reply, setReply] = useState(initialReply || null);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      const res = await tracker.post(`${endpoint}/${hashid}/reply`, { body });
      setReply(res.data);
      setBody('');
      sendNotification({ msg: t('reply_sent_success'), variant: 'success' });
      if (onReplied) onReplied(res.data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        sendNotification({ msg: t('reply_already_exists'), variant: 'warning' });
      } else {
        sendNotification({ msg: t('reply_send_error'), variant: 'error' });
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Box>
      {reply ? (
        // ── Read mode: show existing reply ──────────────────────────────
        <ReadModeReply reply={reply} />
      ) : (
        // ── Edit mode: textarea + send button ──────────────────────────
        <Box>
          {recipientEmail && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {t('reply_will_be_sent_to')}: <strong>{recipientEmail}</strong>
            </Typography>
          )}
          <TextField
            multiline
            minRows={3}
            maxRows={8}
            fullWidth
            size="small"
            placeholder={t('write_your_reply')}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            disabled={sending}
            sx={{ mb: 1.5 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              size="small"
              onClick={handleSend}
              disabled={sending || !body.trim()}
              startIcon={sending ? <CircularProgress size={14} color="inherit" /> : null}
              sx={{ background: '#032C65', '&:hover': { background: '#021a40' } }}
            >
              {sending ? t('sending') : t('send_reply')}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}

function ReadModeReply({ reply }) {
  const { t } = useTranslation();

  const statusIcon = {
    sent:    <CheckCircleOutlineIcon sx={{ fontSize: 15, color: '#6ca181' }} />,
    failed:  <ErrorOutlineIcon      sx={{ fontSize: 15, color: '#ef4444' }} />,
    pending: <ScheduleIcon          sx={{ fontSize: 15, color: '#b07d2e' }} />,
  }[reply.email_status] ?? null;

  const statusLabel = {
    sent:    t('email_sent'),
    failed:  t('email_failed'),
    pending: t('email_pending'),
  }[reply.email_status] ?? reply.email_status;

  const formattedDate = reply.sent_at
    ? new Date(reply.sent_at).toLocaleString()
    : '—';

  return (
    <Box>
      <Divider sx={{ mb: 2 }} />
      <Typography
        variant="subtitle2"
        color="primary"
        fontWeight={700}
        sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: 1, fontSize: 11 }}
      >
        {t('reply_sent')}
      </Typography>

      <Box
        sx={{
          background: '#eef3fb',
          borderLeft: '4px solid #032C65',
          borderRadius: 1,
          p: 2,
          mb: 1.5,
        }}
      >
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {reply.body}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          {t('by')}: <strong>{reply.replied_by_name}</strong>
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t('date')}: <strong>{formattedDate}</strong>
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {statusIcon}
          <Typography variant="caption" color="text.secondary">
            {statusLabel}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
